<template>
  <section id="news" class="news-container section-padding">
    <div class="container">
      <!-- 大白色圆角容器 -->
      <div class="news-module-container">
        <div class="text-center mb-4">
          <h2 class="section-title">协会动态</h2>
          <div class="section-divider w-20 mx-auto"></div>
        </div>
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <!-- 左侧新闻轮播 -->
          <div class="lg:col-span-1 card-base news-left-section">
            <div class="news-carousel-container">
              <div
                v-for="(news, index) in featuredNews"
                :key="index"
                class="news-carousel-slide"
                :class="{ active: currentSlide === index }"
                @click="goToNewsDetail(news.id)"
              >
                <img
                  :src="getFullImageUrl(news.image)"
                  :alt="news.title"
                  class="news-image"
                  loading="lazy"
                  @error="handleImageError"
                >
                <div class="news-content">
                  <div class="news-date">{{ news.date }}</div>
                  <h3 class="news-title">{{ news.title }}</h3>
                  <p class="news-description">{{ news.description }}</p>
                  <a href="javascript:void(0)" class="news-read-more" @click.stop="goToNewsDetail(news.id)">
                    阅读全文 <i class="fa fa-arrow-right"></i>
                  </a>
                </div>
              </div>
              <div class="news-carousel-indicators">
                <div
                  v-for="(news, index) in featuredNews"
                  :key="index"
                  class="news-carousel-indicator"
                  :class="{ active: currentSlide === index }"
                  @click="goToSlide(index)"
                ></div>
              </div>
            </div>
          </div>

          <!-- 右侧标签切换新闻列表 -->
          <div class="lg:col-span-2 card-base news-right-section">
            <div class="news-tabs">
              <div
                v-for="(tab, index) in tabs"
                :key="index"
                class="news-tab"
                :class="{ active: activeTab === index }"
                @click="activeTab = index"
              >
                {{ tab }}
              </div>
            </div>

            <div class="news-list">
              <div v-if="loading" class="loading-text">加载中...</div>
              <div v-else-if="!newsItems[activeTab] || newsItems[activeTab].length === 0" class="empty-text">暂无数据</div>
              <div
                v-else
                v-for="(item, index) in newsItems[activeTab].slice(0, 6)"
                :key="index"
                class="news-item"
                @click="goToNewsDetail(item.id)"
              >
                <div class="news-item-date">
                  <div class="news-item-day">{{ item.date.day }}</div>
                  <div class="news-item-month">{{ item.date.month }}</div>
                </div>
                <div class="news-item-content">
                  <div class="news-item-title">
                    {{ item.title }}
                    <i class="fa fa-angle-right news-item-arrow"></i>
                  </div>
                </div>
              </div>
            </div>

            <button class="news-more-btn" @click="goToNoticeList">
              查看更多 <i class="fa fa-arrow-right ml-2"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { api } from '../api'
import { CURRENT_CONFIG } from '../api/config';
export default {
  name: 'NewsSection',
  data() {
    return {
      activeTab: 0,
      tabs: [],
      categories: [],
      featuredNews: [], // 清空静态数据，改为从API获取
      currentSlide: 0,
      interval: null,
      newsItems: {},
      loading: false,
      activityCategoryId: null, // 活动报道分类ID
      BASE_URL: CURRENT_CONFIG.BASE_URL
    }
  },
  async mounted() {
    this.startCarousel()
    await this.loadCategories()
    await this.loadFeaturedNews() // 加载顶置文章
  },
  beforeUnmount() {
    this.stopCarousel()
  },
  watch: {
    activeTab() {
      this.loadNewsItems()
    }
  },
  methods: {
    async loadCategories() {
      try {
        this.loading = true
        const categories = await api.getCategories()
        const newsCategories = categories.filter(cat => cat.firstLevelModule === '协会动态')
        this.categories = newsCategories
        this.tabs = newsCategories.map(cat => cat.secondLevelModule)

        // 找到活动报道分类ID
        const activityCategory = newsCategories.find(cat => cat.secondLevelModule === '活动报道')
        if (activityCategory) {
          this.activityCategoryId = activityCategory.id
        }

        await this.loadNewsItems()
      } catch (error) {
        console.error('加载分类失败:', error)
      } finally {
        this.loading = false
      }
    },
    async loadFeaturedNews() {
      // 加载活动报道的所有顶置文章
      if (!this.activityCategoryId) return

      try {
        const result = await api.getArticles(this.activityCategoryId, '', 1, 100, true) // top=true，pageSize=100
        this.featuredNews = result.rows.map(article => ({
          id: article.id,
          title: article.title || '暂无标题',
          image: article.coverImageUrl || `https://picsum.photos/seed/${article.id}/800/400`,
          description: article.summary || article.content?.substring(0, 100) + '...' || '暂无描述',
          date: article.publishedTime ? new Date(article.publishedTime).toLocaleDateString('zh-CN') : '未知时间'
        }))
      } catch (error) {
        console.error('加载顶置文章失败:', error)
        this.featuredNews = []
      }
    },
    async loadNewsItems() {
      if (this.categories.length === 0 || this.activeTab >= this.categories.length) return

      try {
        const categoryId = this.categories[this.activeTab].id
        const result = await api.getArticles(categoryId)
        this.newsItems[this.activeTab] = result.rows.map(article => ({
          id: article.id,
          title: article.title || '暂无标题',
          date: this.formatDate(article.publishedTime)
        }))
      } catch (error) {
        console.error('加载文章列表失败:', error)
        this.newsItems[this.activeTab] = []
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return { day: '--', month: '--月' }
      const date = new Date(dateStr)
      return {
        day: String(date.getDate()).padStart(2, '0'),
        month: `${String(date.getMonth() + 1).padStart(2, '0')}月`
      }
    },
    goToNewsDetail(id) {
      this.$router.push(`/article/${id}`)
    },
    goToNoticeList() {
      if (this.categories.length === 0 || this.activeTab >= this.categories.length) return
      const categoryName = this.tabs[this.activeTab]
      this.$router.push(`/notices?category=${encodeURIComponent(categoryName)}`)
    },
    nextSlide() {
      this.currentSlide = (this.currentSlide + 1) % this.featuredNews.length
    },
    goToSlide(index) {
      this.currentSlide = index
      this.stopCarousel()
      this.startCarousel()
    },
    startCarousel() {
      this.interval = setInterval(() => {
        this.nextSlide()
      }, 5000)
    },
    stopCarousel() {
      if (this.interval) {
        clearInterval(this.interval)
        this.interval = null
      }
    },
    getFullImageUrl(path) {
      if (!path) return require('../assets/default_logo.jpg')
      if (path.startsWith('http')) return path
      return this.BASE_URL + path
    },
    handleImageError(event) {
      event.target.src = require('../assets/default_logo.jpg')
    }
  }
}
</script>

<style scoped>
.news-container {
  background-color: var(--bg);
  padding: 16px 0;
}

/* 大白色圆角容器 */
.news-module-container {
  background: #ffffff;
  border-radius: 32px;
  box-shadow: 0 20px 40px -15px rgba(37,99,235,0.08);
  padding: 48px 40px;
  margin-bottom: 0;
  border: 1px solid rgba(37,99,235,0.12);
}

.news-left-section {
  height: 550px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--surface);
  backdrop-filter: blur(20px) saturate(120%);
  -webkit-backdrop-filter: blur(20px) saturate(120%);
  border-radius: var(--radius-2xl);
  box-shadow: var(--shadow);
  border: 1px solid var(--border);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.news-left-section:hover {
  /*transform: translateY(-8px) scale(1.015);*/
  box-shadow: 0 15px 35px -10px rgba(37, 99, 235, 0.15), 0 0 20px rgba(96, 165, 250, 0.2);
  border-color: rgba(96, 165, 250, 0.3);
}

.news-carousel-container {
  height: 100%;
  position: relative;
}

.news-carousel-slide {
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity 0.5s ease-in-out;
  display: flex;
  flex-direction: column;
}

.news-carousel-slide.active {
  opacity: 1;
}

.news-image {
  width: 100%;
  height: 280px;
  object-fit: cover;
  transition: transform 0.3s ease;
  border-radius: var(--radius-2xl) var(--radius-2xl) 0 0;
}

.news-left-section:hover .news-image {
  /*transform: scale(1.02);*/
}

.news-carousel-indicators {
  position: absolute;
  bottom: 12px;
  left: 50%;
  /*transform: translateX(-50%);*/
  display: flex;
  gap: 8px;
  z-index: 10;
}

.news-carousel-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: rgba(148, 163, 184, 0.3);
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid var(--border);
}

.news-carousel-indicator:hover {
  background-color: rgba(96, 165, 250, 0.6);
  /*transform: scale(1.1);*/
}

.news-carousel-indicator.active {
  background-color: var(--accent);
  /*transform: scale(1.2);*/
  box-shadow: 0 0 10px rgba(96, 165, 250, 0.5);
}

.news-content {
  padding: 24px;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.news-date {
  color: var(--text-secondary);
  font-size: 14px;
  margin-bottom: 12px;
}

.news-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 12px;
  line-height: 1.4;
}

.news-description {
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 16px;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.news-read-more {
  color: var(--accent);
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  transition: color 0.3s ease;
}

.news-read-more:hover {
  color: var(--accent-light);
  text-shadow: 0 0 10px rgba(96, 165, 250, 0.5);
}

.news-read-more i {
  margin-left: 6px;
  transition: transform 0.3s ease;
}

.news-read-more:hover i {
  /*transform: translateX(2px);*/
  color: var(--accent-light);
}

.news-right-section {
  min-height: 550px;
  display: flex;
  flex-direction: column;
  background: var(--surface);
  backdrop-filter: blur(20px) saturate(120%);
  -webkit-backdrop-filter: blur(20px) saturate(120%);
  border-radius: var(--radius-2xl);
  box-shadow: var(--shadow);
  border: 1px solid var(--border);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.news-right-section:hover {
  /*transform: translateY(-8px) scale(1.015);*/
  box-shadow: 0 15px 35px -10px rgba(37, 99, 235, 0.15), 0 0 20px rgba(96, 165, 250, 0.2);
  border-color: rgba(96, 165, 250, 0.3);
}

.news-tabs {
  display: flex;
  padding: 20px 24px 0;
  border-bottom: 1px solid var(--border);
}

.news-tab {
  padding: 12px 20px;
  font-size: 16px;
  font-weight: 500;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
}

.news-tab:hover {
  color: var(--accent);
}

.news-tab.active {
  color: var(--accent);
  border-bottom-color: var(--accent);
  box-shadow: 0 0 15px rgba(96, 165, 250, 0.3);
  border-radius: var(--radius-md) var(--radius-md) 0 0;
  background: rgba(96, 165, 250, 0.1);
}

.news-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(3, auto);
  gap: 34px;
  flex: 1;
  padding: 24px;
}

.news-item {
  display: flex;
  align-items: center;
  padding: 16px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px) saturate(120%);
  -webkit-backdrop-filter: blur(20px) saturate(120%);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow);
  border: 1px solid var(--border);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  border-left: 3px solid transparent;
  min-width: 200px;
}

.news-item:hover {
  /*transform: translateY(-8px) scale(1.015);*/
  box-shadow: 0 15px 35px -10px rgba(37, 99, 235, 0.15), 0 0 20px rgba(96, 165, 250, 0.2);
  border-left-color: var(--accent);
  border-color: rgba(96, 165, 250, 0.3);
}

.news-item-date {
  background: rgba(255, 255, 255, 0.8);
  border-radius: var(--radius-md);
  padding: 12px;
  text-align: center;
  min-width: 60px;
  margin-right: 16px;
  border: 1px solid var(--border);
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.1);
}

.news-item-day {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  line-height: 1;
}

.news-item-month {
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 4px;
}

.news-item-content {
  flex: 1;
}

.news-item-title {
  font-size: 15px;
  font-weight: 500;
  color: var(--text-primary);
  line-height: 1.4;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.news-item-arrow {
  color: var(--text-secondary);
  font-size: 14px;
  transition: all 0.3s ease;
}

.news-item:hover .news-item-arrow {
  color: var(--accent);
  transform: translateX(2px);
}

.news-more-btn {
  background: linear-gradient(135deg, var(--accent), var(--accent-light));
  color: white;
  padding: 12px 32px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-flex;
  align-items: center;
  margin: 0 24px 24px;
  align-self: center;
  box-shadow: 0 4px 15px rgba(37, 99, 235, 0.3);
  position: relative;
  overflow: hidden;
}

.news-more-btn::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transform: rotate(45deg);
  animation: shine 3s infinite;
  opacity: 0;
}

@keyframes shine {
  0% {
    transform: translateX(-100%) rotate(45deg);
    opacity: 0;
  }
  50% {
    opacity: 0.3;
  }
  100% {
    transform: translateX(100%) rotate(45deg);
    opacity: 0;
  }
}

.news-more-btn:hover {
  /*transform: translateY(-4px) scale(1.05);*/
  box-shadow: 0 8px 24px rgba(37, 99, 235, 0.4);
  animation: breathe 2s ease-in-out infinite;
}

.news-more-btn:hover::before {
  animation: shine 1.5s infinite;
}

@keyframes breathe {
  0%, 100% {
    box-shadow: 0 6px 20px rgba(37, 99, 235, 0.4);
  }
  50% {
    box-shadow: 0 8px 25px rgba(37, 99, 235, 0.6);
  }
}

.loading-text,
.empty-text {
  grid-column: 1 / -1;
  text-align: center;
  padding: 40px 20px;
  color: var(--text-secondary);
  font-size: 14px;
  background: var(--surface);
  backdrop-filter: blur(20px) saturate(120%);
  -webkit-backdrop-filter: blur(20px) saturate(120%);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow);
  border: 1px solid var(--border);
}

/* 响应式设计 */
@media (max-width: 1024px) {
  .news-tabs {
    flex-wrap: wrap;
  }

  .news-tab {
    padding: 8px 16px;
    font-size: 14px;
  }
}

@media (max-width: 768px) {
  .news-left-section {
      height: 400px;
    }

    .news-list {
    grid-template-columns: 1fr;
    grid-template-rows: repeat(6, auto);
    gap: 16px;
  }

  .news-item {
    flex-direction: column;
    align-items: flex-start;
    padding: 16px;
  }

  .news-item-date {
    margin-right: 0;
    margin-bottom: 12px;
    min-width: auto;
  }

  .news-title {
    font-size: 16px;
  }

  .news-description {
    font-size: 13px;
  }
}
</style>
